import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const PRESET_COLORS = [
  "#F44336", "#E91E63", "#9C27B0", "#3F51B5",
  "#2196F3", "#00BCD4", "#4CAF50", "#FF9800",
  "#795548", "#607D8B", "#000000", "#9E9E9E",
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 700,
    margin: "0 auto",
  },
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  stateRow: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    display: "inline-block",
    marginRight: 8,
    flexShrink: 0,
    verticalAlign: "middle",
  },
  systemBadge: {
    marginLeft: 8,
    height: 16,
    fontSize: 9,
    fontWeight: 700,
    backgroundColor: theme.palette.action.selected,
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  colorPresets: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 8,
    marginBottom: 4,
  },
  colorSwatch: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "transform 0.1s",
    "&:hover": { transform: "scale(1.2)" },
  },
  colorSwatchSelected: {
    border: "2px solid #000",
    transform: "scale(1.15)",
  },
  actionGroup: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  settingsRow: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    marginBottom: theme.spacing(2),
  },
  settingsDesc: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginLeft: 52,
    marginTop: -6,
  },
}));

const StateModal = ({ open, onClose, onSave, initial }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#9E9E9E");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setColor(initial?.color || "#9E9E9E");
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!name.trim()) { toast.warning(i18n.t("contactStates.nombreObligatorio")); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), color });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? i18n.t("contactStates.editarEstado") : i18n.t("contactStates.nuevoEstado")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label={i18n.t("contactStates.nombre")}
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          size="small"
          variant="outlined"
          style={{ marginBottom: 16 }}
          onKeyDown={e => e.key === "Enter" && handleSave()}
        />
        <Typography variant="caption" color="textSecondary">{i18n.t("tags.color")}</Typography>
        <div className={classes.colorPresets}>
          {PRESET_COLORS.map(c => (
            <span
              key={c}
              className={`${classes.colorSwatch} ${color === c ? classes.colorSwatchSelected : ""}`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
              title={c}
            />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            style={{ width: 36, height: 36, border: "none", padding: 0, cursor: "pointer", borderRadius: 4 }}
          />
          <Typography variant="body2">{color}</Typography>
          <span style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: color, border: "1px solid rgba(0,0,0,0.2)", flexShrink: 0 }} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">{i18n.t("contactStates.cancelar")}</Button>
        <Button onClick={handleSave} variant="contained" color="primary" size="small" disabled={saving}>
          {saving ? <CircularProgress size={16} /> : i18n.t("contactStates.guardar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReasonModal = ({ open, onClose, onSave, initial }) => {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setName(initial?.name || "");
  }, [open, initial]);

  const handleSave = async () => {
    if (!name.trim()) { toast.warning(i18n.t("contactStates.nombreObligatorio")); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim() });
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{initial ? i18n.t("contactStates.editarMotivo") : i18n.t("contactStates.nuevoMotivo")}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          label={i18n.t("contactStates.nombre")}
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          size="small"
          variant="outlined"
          onKeyDown={e => e.key === "Enter" && handleSave()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">{i18n.t("contactStates.cancelar")}</Button>
        <Button onClick={handleSave} variant="contained" color="primary" size="small" disabled={saving}>
          {saving ? <CircularProgress size={16} /> : i18n.t("contactStates.guardar")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ContactStatesAdmin = () => {
  const classes = useStyles();

  const [states, setStates] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [closeOnGained, setCloseOnGained] = useState(true);
  const [closeOnLost, setCloseOnLost] = useState(true);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [stateModal, setStateModal] = useState({ open: false, editing: null });
  const [reasonModal, setReasonModal] = useState({ open: false, editing: null });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, type: null, item: null });

  const loadAll = useCallback(async () => {
    try {
      const [statesRes, reasonsRes, settingsRes] = await Promise.all([
        api.get("/contact-states?all=1"),
        api.get("/lost-reasons?all=1"),
        api.get("/companies/close-settings"),
      ]);
      setStates(statesRes.data);
      setReasons(reasonsRes.data);
      setCloseOnGained(settingsRes.data.closeOnGained !== false);
      setCloseOnLost(settingsRes.data.closeOnLost !== false);
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSaveState = async ({ name, color }) => {
    if (stateModal.editing) {
      await api.put(`/contact-states/${stateModal.editing.id}`, { name, color });
      toast.success(i18n.t("contactStates.estadoActualizado"));
    } else {
      await api.post("/contact-states", { name, color });
      toast.success(i18n.t("contactStates.estadoCreado"));
    }
    await loadAll();
  };

  const handleToggleStateActive = async (state) => {
    try {
      await api.put(`/contact-states/${state.id}`, { active: !state.active });
      setStates(prev => prev.map(s => s.id === state.id ? { ...s, active: state.active ? 0 : 1 } : s));
    } catch (err) { toastError(err); }
  };

  const handleDeleteState = async () => {
    try {
      await api.delete(`/contact-states/${confirmDelete.item.id}`);
      toast.success(i18n.t("contactStates.estadoEliminado"));
      setConfirmDelete({ open: false, type: null, item: null });
      await loadAll();
    } catch (err) { toastError(err); }
  };

  const moveState = async (idx, dir) => {
    const newStates = [...states];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newStates.length) return;
    [newStates[idx], newStates[swapIdx]] = [newStates[swapIdx], newStates[idx]];
    setStates(newStates);
    try {
      await api.put("/contact-states/reorder", { order: newStates.map(s => s.id) });
    } catch (err) { toastError(err); await loadAll(); }
  };

  const handleSaveReason = async ({ name }) => {
    if (reasonModal.editing) {
      await api.put(`/lost-reasons/${reasonModal.editing.id}`, { name });
      toast.success(i18n.t("contactStates.motivoActualizado"));
    } else {
      await api.post("/lost-reasons", { name });
      toast.success(i18n.t("contactStates.motivoCreado"));
    }
    await loadAll();
  };

  const handleToggleReasonActive = async (reason) => {
    try {
      await api.put(`/lost-reasons/${reason.id}`, { active: !reason.active });
      setReasons(prev => prev.map(r => r.id === reason.id ? { ...r, active: reason.active ? 0 : 1 } : r));
    } catch (err) { toastError(err); }
  };

  const handleDeleteReason = async () => {
    try {
      await api.delete(`/lost-reasons/${confirmDelete.item.id}`);
      toast.success(i18n.t("contactStates.motivoEliminado"));
      setConfirmDelete({ open: false, type: null, item: null });
      await loadAll();
    } catch (err) { toastError(err); }
  };

  const handleSaveCloseSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/companies/close-settings", { closeOnGained, closeOnLost });
      toast.success(i18n.t("contactStates.configuracionGuardada"));
    } catch (err) { toastError(err); }
    setSavingSettings(false);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 24 }}>
        {i18n.t("contactStates.titulo")}
      </Typography>

      <Paper className={classes.section} variant="outlined">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("contactStates.estadosContacto")}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setStateModal({ open: true, editing: null })}
          >
            {i18n.t("contactStates.agregarEstado")}
          </Button>
        </div>
        <List disablePadding>
          {states.map((state, idx) => (
            <ListItem key={state.id} className={classes.stateRow} dense>
              <span className={classes.dot} style={{ backgroundColor: state.color }} />
              <ListItemText
                primary={
                  <span style={{ display: "flex", alignItems: "center", opacity: state.active ? 1 : 0.45 }}>
                    {state.name}
                    {!!state.isSystem && (
                      <Chip label={i18n.t("contactStates.sistema")} className={classes.systemBadge} size="small" />
                    )}
                  </span>
                }
              />
              <ListItemSecondaryAction className={classes.actionGroup}>
                <Tooltip
                  title={
                    state.isSystem
                      ? i18n.t("contactStates.ocultarSistema")
                      : state.active
                      ? i18n.t("contactStates.desactivar")
                      : i18n.t("contactStates.activar")
                  }
                >
                  <Switch
                    size="small"
                    checked={!!state.active}
                    onChange={() => handleToggleStateActive(state)}
                    color="primary"
                  />
                </Tooltip>
                <Tooltip title={i18n.t("contactStates.subir")}>
                  <span>
                    <IconButton size="small" onClick={() => moveState(idx, -1)} disabled={idx === 0}>
                      <ArrowUpwardIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={i18n.t("contactStates.bajar")}>
                  <span>
                    <IconButton size="small" onClick={() => moveState(idx, 1)} disabled={idx === states.length - 1}>
                      <ArrowDownwardIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={i18n.t("contactStates.editar")}>
                  <IconButton size="small" onClick={() => setStateModal({ open: true, editing: state })}>
                    <EditIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={state.isSystem ? i18n.t("contactStates.noEliminarSistema") : i18n.t("contactStates.eliminar")}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!!state.isSystem}
                      onClick={() => setConfirmDelete({ open: true, type: "state", item: state })}
                    >
                      <DeleteIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper className={classes.section} variant="outlined">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("contactStates.motivosPerdida")}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setReasonModal({ open: true, editing: null })}
          >
            {i18n.t("contactStates.agregarMotivo")}
          </Button>
        </div>
        {reasons.length === 0 && (
          <Typography variant="body2" color="textSecondary" style={{ padding: "8px 0" }}>
            {i18n.t("contactStates.noHayMotivos")}
          </Typography>
        )}
        <List disablePadding>
          {reasons.map((reason) => (
            <ListItem key={reason.id} className={classes.stateRow} dense>
              <ListItemText primary={<span style={{ opacity: reason.active ? 1 : 0.45 }}>{reason.name}</span>} />
              <ListItemSecondaryAction className={classes.actionGroup}>
                <Tooltip title={reason.active ? i18n.t("contactStates.desactivar") : i18n.t("contactStates.activar")}>
                  <Switch
                    size="small"
                    checked={!!reason.active}
                    onChange={() => handleToggleReasonActive(reason)}
                    color="primary"
                  />
                </Tooltip>
                <Tooltip title={i18n.t("contactStates.editar")}>
                  <IconButton size="small" onClick={() => setReasonModal({ open: true, editing: reason })}>
                    <EditIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={i18n.t("contactStates.eliminar")}>
                  <IconButton size="small" onClick={() => setConfirmDelete({ open: true, type: "reason", item: reason })}>
                    <DeleteIcon style={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper className={classes.section} variant="outlined">
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("contactStates.cierreAutomatico")}
        </Typography>
        <div className={classes.settingsRow}>
          <FormControlLabel
            control={<Switch checked={closeOnGained} onChange={e => setCloseOnGained(e.target.checked)} color="primary" />}
            label={i18n.t("contactStates.cerrarGanado")}
          />
          <Typography variant="body2" className={classes.settingsDesc}>
            {i18n.t("contactStates.descCerrarGanado")}
          </Typography>
        </div>
        <div className={classes.settingsRow}>
          <FormControlLabel
            control={<Switch checked={closeOnLost} onChange={e => setCloseOnLost(e.target.checked)} color="primary" />}
            label={i18n.t("contactStates.cerrarPerdido")}
          />
          <Typography variant="body2" className={classes.settingsDesc}>
            {i18n.t("contactStates.descCerrarPerdido")}
          </Typography>
        </div>
        <Button variant="contained" color="primary" size="small" onClick={handleSaveCloseSettings} disabled={savingSettings}>
          {savingSettings ? <CircularProgress size={16} /> : i18n.t("contactStates.guardarConfig")}
        </Button>
      </Paper>

      <StateModal
        open={stateModal.open}
        onClose={() => setStateModal({ open: false, editing: null })}
        onSave={handleSaveState}
        initial={stateModal.editing}
      />
      <ReasonModal
        open={reasonModal.open}
        onClose={() => setReasonModal({ open: false, editing: null })}
        onSave={handleSaveReason}
        initial={reasonModal.editing}
      />

      <Dialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, type: null, item: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18n.t("contactStates.confirmarEliminacion")}</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDelete.type === "state"
              ? i18n.t("contactStates.eliminarEstado")
              : i18n.t("contactStates.eliminarMotivo")}
          </Typography>
          {confirmDelete.item && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              <strong>{confirmDelete.item.name}</strong>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete({ open: false, type: null, item: null })} size="small">
            {i18n.t("contactStates.cancelar")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={confirmDelete.type === "state" ? handleDeleteState : handleDeleteReason}
          >
            {i18n.t("contactStates.eliminar")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContactStatesAdmin;
